import { useMemo, useState } from 'react';
import { App } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  PlatformSettingsApiError,
  PlatformSettingsService,
} from '~/app/services/settings/platform-settings.service';
import type {
  DisplayPlatformSetting,
  SettingDrafts,
  SettingUpdateInput,
  SettingValue,
} from '../types';
import { iziviaMonprojetCronSchedulesMeetMinimumSeparation } from '@evimo/core/libs/schedule/izivia-monprojet-cron-schedule.validation';
import {
  deriveSettingTranslationKey,
  filterAndGroupSettings,
  normalizeReason,
  normalizeSettingDraftValueSafely,
  normalizeSettingSubmissionValue,
  resolveIziviaPeerCronScheduleKey,
  resolveSettingsErrorMessageKey,
  updateDrafts,
  validateCronExpression,
} from '../utils';

const listQueryKey = ['platform-settings', 'list'] as const;

export function usePlatformSettings() {
  const { t } = useTranslation('settings');
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('ALL');
  const [drafts, setDrafts] = useState<SettingDrafts>({});
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [historyKey, setHistoryKey] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const listQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: () => PlatformSettingsService.list(),
  });

  const settings = useMemo<DisplayPlatformSetting[]>(
    () =>
      (listQuery.data?.data ?? []).map((setting) => {
        const label = t(deriveSettingTranslationKey(setting.key, 'label'), {
          defaultValue: setting.key,
        });
        const description = t(
          deriveSettingTranslationKey(setting.key, 'description'),
          { defaultValue: t('metadata.noDescription') },
        );
        return {
          ...setting,
          searchText: `${label} ${description}`,
        };
      }),
    [listQuery.data?.data, t],
  );

  const groups = useMemo(
    () => filterAndGroupSettings(settings, search, category),
    [category, search, settings],
  );
  const categories = useMemo(
    () => [...new Set(settings.map((setting) => setting.category))].sort(),
    [settings],
  );
  const pendingSetting =
    settings.find((setting) => setting.key === pendingKey) ?? null;
  const dirtyCount = Object.keys(drafts).length;

  const updateMutation = useMutation({
    mutationFn: (input: SettingUpdateInput) =>
      PlatformSettingsService.update(input.key, {
        value: input.value,
        expectedVersion: input.expectedVersion,
        ...(input.reason ? { reason: input.reason } : {}),
      }),
    onSuccess: async (authoritativeSetting, input) => {
      setDrafts((current) =>
        Object.fromEntries(
          Object.entries(current).filter(
            ([draftKey]) => draftKey !== input.key,
          ),
        ),
      );
      setPendingKey(null);
      setReason('');
      queryClient.setQueryData(
        ['platform-settings', 'detail', input.key],
        authoritativeSetting,
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: listQueryKey,
          exact: true,
          refetchType: 'none',
        }),
        queryClient.invalidateQueries({
          queryKey: ['platform-settings', 'history', input.key],
          exact: true,
          refetchType: 'none',
        }),
        queryClient.invalidateQueries({
          queryKey: ['platform-settings', 'detail', input.key],
          exact: true,
          refetchType: 'none',
        }),
      ]);
      await Promise.all([
        queryClient.refetchQueries({
          queryKey: listQueryKey,
          exact: true,
          type: 'active',
        }),
        queryClient.refetchQueries({
          queryKey: ['platform-settings', 'history', input.key],
          exact: true,
          type: 'active',
        }),
        queryClient.fetchQuery({
          queryKey: ['platform-settings', 'detail', input.key],
          queryFn: () => PlatformSettingsService.get(input.key),
        }),
      ]);
      message.success(t('messages.updateSuccess'));
    },
    onError: async (error, input) => {
      const status =
        error instanceof PlatformSettingsApiError ? error.status : undefined;
      message.error(t(resolveSettingsErrorMessageKey(status)));
      if (status === 409) {
        setPendingKey(null);
        setReason('');
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: listQueryKey,
            exact: true,
            refetchType: 'none',
          }),
          queryClient.invalidateQueries({
            queryKey: ['platform-settings', 'detail', input.key],
            exact: true,
            refetchType: 'none',
          }),
        ]);
        await Promise.all([
          queryClient.refetchQueries({
            queryKey: listQueryKey,
            exact: true,
            type: 'active',
          }),
          queryClient.fetchQuery({
            queryKey: ['platform-settings', 'detail', input.key],
            queryFn: () => PlatformSettingsService.get(input.key),
          }),
        ]);
      }
    },
  });

  function onDraftChange(
    setting: DisplayPlatformSetting,
    input: unknown,
  ): void {
    const value = normalizeSettingDraftValueSafely(setting.valueType, input);
    if (value === undefined) return;
    setDrafts((current) =>
      updateDrafts(current, setting.key, value, setting.value),
    );
  }

  function onRequestApply(key: string): void {
    setPendingKey(key);
    setReason('');
  }

  function onConfirmApply(): void {
    if (!pendingSetting || !(pendingSetting.key in drafts)) return;
    const draftValue = drafts[pendingSetting.key] as SettingValue;
    const submissionValue = normalizeSettingSubmissionValue(
      pendingSetting.valueType,
      draftValue,
    );
    if (
      pendingSetting.valueType === 'CRON' &&
      (typeof submissionValue !== 'string' ||
        !validateCronExpression(submissionValue))
    ) {
      message.error(t('cron.validationError'));
      return;
    }
    const iziviaPeerKey = resolveIziviaPeerCronScheduleKey(pendingSetting.key);
    if (
      iziviaPeerKey &&
      typeof submissionValue === 'string' &&
      validateCronExpression(submissionValue)
    ) {
      const peerSetting = settings.find(
        (setting) => setting.key === iziviaPeerKey,
      );
      const peerSchedule =
        (drafts[iziviaPeerKey] as string | undefined) ??
        (typeof peerSetting?.value === 'string' ? peerSetting.value : '');
      if (
        peerSchedule &&
        validateCronExpression(peerSchedule) &&
        !iziviaMonprojetCronSchedulesMeetMinimumSeparation(
          pendingSetting.key === 'izivia-monprojet-cases:cron:schedule'
            ? submissionValue
            : peerSchedule,
          pendingSetting.key === 'izivia-monprojet-workorders:cron:schedule'
            ? submissionValue
            : peerSchedule,
        )
      ) {
        message.error(t('cron.iziviaSeparationError'));
        return;
      }
    }
    updateMutation.mutate({
      key: pendingSetting.key,
      value: submissionValue,
      expectedVersion: pendingSetting.version,
      reason: normalizeReason(reason),
    });
  }

  return {
    categories,
    category,
    drafts,
    dirtyCount,
    groups,
    historyKey,
    isLoading: listQuery.isLoading,
    isRefreshing: listQuery.isFetching,
    isUpdating: updateMutation.isPending,
    listError: listQuery.error,
    pendingSetting,
    reason,
    search,
    setCategory,
    setHistoryKey,
    setReason,
    setSearch,
    onConfirmApply,
    onDiscardAll: () => setDrafts({}),
    onDraftChange,
    onRequestApply,
    onRefresh: () => listQuery.refetch(),
    onCancelApply: () => {
      if (!updateMutation.isPending) {
        setPendingKey(null);
        setReason('');
      }
    },
  };
}
